import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { intersection, values } from 'lodash';
import { useTranslation } from 'react-i18next';
import { logoutRequest } from 'actions/sessionActions';
import { setLanguage } from 'actions/shellActions';
import { LanguagePicker } from '../languagePicker/LanguagePicker';
import { useDeviceTypes, useSession } from 'hooks';
import { getBuildings } from 'selectors/buildingsSelectors';
import { CuratedContentDialog } from '../curatedContentDialog/CuratedContentDialog';
import { analytics } from 'util/analytics';
import { loadLanguage } from 'lang/loadLanguage';
import { getThemes } from 'selectors/themesSelectors';
import { State } from 'reducers';
import { BuiltInRoleIds } from 'shared/permissions';
import { DeviceClass, DeviceFamily } from 'shared/models';

require('./BlackBar.css');

export const BlackBar = () => {
    const { t } = useTranslation();
    const { user } = useSession();
    const { deviceTypes } = useDeviceTypes({ deviceClass: DeviceClass.Display });
    const buildings = values(useSelector(getBuildings));
    const themes = values(useSelector((state: State) => getThemes(state)));

    const dispatch = useDispatch();

    const handleClickLogout = useCallback(() => dispatch(logoutRequest()), [ dispatch ]);
    const handleClickLanguage = useCallback((langCode: string) => {
        dispatch(setLanguage(langCode));
        loadLanguage(langCode);
    }, [ dispatch ]);

    // Matisse and MatisseFlex device types that are > 5" and < 65"
    const validDeviceTypeIdsForElev8Popup = values(deviceTypes).filter(dt => {
        if (!dt.deviceFamily || !dt.deviceFamily!.toLowerCase().includes('matisse')) return false;
        if (!dt.physicalWidth || !dt.physicalHeight) return false;
        const diagonalInches = Math.round(Math.sqrt(dt.physicalWidth * dt.physicalWidth + dt.physicalHeight * dt.physicalHeight));
        return diagonalInches > 5 && diagonalInches < 65;
    }).map(dt => dt.id);

    const hasVerticalCityTheme = themes && /https:\/\/elev8|verticalcity.com/i.test(JSON.stringify(themes));
    
    const excludedRoleIds = [
        BuiltInRoleIds.OemAdministrator,
        BuiltInRoleIds.OemManager,
        BuiltInRoleIds.OemVideoMonitor,
        BuiltInRoleIds.MadTechSupport,
        BuiltInRoleIds.SiteAdministrator,
    ];

    const isLoggedIn = !!user;
    const hasThemesEligibleForElev8Popup = !!themes?.some(theme =>
        theme.requirements.Matisse ||
        validDeviceTypeIdsForElev8Popup.indexOf(theme.requirements.deviceTypeId || '') !== -1
    );
    const seenAd = !!window.localStorage.getItem('M1-2310');
    const isTargetUser = isLoggedIn && !!user.roleIds?.length && !intersection(user.roleIds, excludedRoleIds).length;
    const isNorthAmerica = buildings && buildings.length > 0 && !buildings.some(b => b.country !== 'US' && b.country !== 'CA');

    const isEligibleForCuratedContent = isNorthAmerica && isTargetUser && hasThemesEligibleForElev8Popup && !hasVerticalCityTheme;
    const [ curatedContentDialogVisible, setCuratedContentDialogVisible ] = useState(false);

    useEffect(() => {
        if (isEligibleForCuratedContent && !seenAd) {
            setCuratedContentDialogVisible(true);
            try {
                window.localStorage.setItem('M1-2310', '1');
                analytics.event('Elev8 Curated Content', 'popup');
            }
            catch (err) {
                console.error(err);
            }
        }
    }, [ isEligibleForCuratedContent, seenAd, setCuratedContentDialogVisible ]);

    return (
        <div id="black-bar">
            <nav>
                <LanguagePicker onLanguageChange={ handleClickLanguage }/>

                { isLoggedIn &&
                    <Fragment>
                        <Link to='/profile'>
                            { t('PROFILE.TITLE') }
                        </Link>
                        
                        {isEligibleForCuratedContent &&
                            <CuratedContentDialog
                                visible={ curatedContentDialogVisible }
                                onClose={ () => setCuratedContentDialogVisible(false) }
                            />
                        }

                        <Link to='/help'>
                            { t('HELP.TITLE') }
                        </Link>

                        <Link to='#' onClick={ handleClickLogout }>
                            { t('COMMAND.LOGOUT') }
                        </Link>
                    </Fragment>
                }
            </nav>
        </div>
    );
};
